import React, {useEffect, useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Box,
  Card,
  Typography,
  CircularProgress,
} from '@mui/material';
import {Construction} from '@mui/icons-material';
import {DatePicker} from '@mui/x-date-pickers';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from 'dayjs';
import 'dayjs/locale/de';
import {UrlaubsantragService} from '../../../../services/openapi/services/UrlaubsantragService';
import {
  UrlaubsantragWithID,
  UrlaubsantragEditData,
} from '../../../../Contexts/Urlaubsantrag/UrlaubsantragContext';

interface UrlaubsantragEditDialogProps {
  open: boolean;
  antrag: UrlaubsantragWithID | null;
  loading: boolean;
  onClose: () => void;
  onSave: (id: number, data: UrlaubsantragEditData) => Promise<void>;
}

export const UrlaubsantragEditDialog: React.FC<UrlaubsantragEditDialogProps> = ({
  open,
  antrag,
  loading,
  onClose,
  onSave,
}) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [grund, setGrund] = useState('');
  const [notiz, setNotiz] = useState('');
  const [workingDays, setWorkingDays] = useState<number | null>(null);

  // Prefill when the selected request changes
  useEffect(() => {
    if (antrag) {
      setStartDate(dayjs(antrag.beginn));
      setEndDate(dayjs(antrag.ende));
      setGrund(antrag.grund || '');
      setNotiz(antrag.notiz || '');
      setWorkingDays(null);
    }
  }, [antrag]);

  // Live "X Arbeitstage" preview via the existing calculate-days endpoint
  useEffect(() => {
    const calc = async () => {
      if (startDate && endDate && !endDate.isBefore(startDate, 'day')) {
        try {
          const res =
            await UrlaubsantragService.calculateVacationDaysUrlaubsantragCalculateDaysPost(
              startDate.format('YYYY-MM-DD'),
              endDate.format('YYYY-MM-DD')
            );
          setWorkingDays(res.working_days);
        } catch {
          setWorkingDays(null);
        }
      } else {
        setWorkingDays(null);
      }
    };
    calc();
  }, [startDate, endDate]);

  const invalidRange = !startDate || !endDate || endDate.isBefore(startDate, 'day');

  const handleSave = async () => {
    if (!antrag || invalidRange) return;
    await onSave(antrag.id, {
      beginn: startDate!.format('YYYY-MM-DD'),
      ende: endDate!.format('YYYY-MM-DD'),
      grund: grund || undefined,
      notiz: notiz || undefined,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        Urlaubsantrag bearbeiten{antrag ? ` – ${antrag.username}` : ''}
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
          <Grid container spacing={3} sx={{mt: 0.5}}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label='Von'
                value={startDate}
                format='DD.MM.YYYY'
                onChange={(v) => setStartDate(v)}
                slotProps={{textField: {fullWidth: true}}}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label='Bis'
                value={endDate}
                format='DD.MM.YYYY'
                minDate={startDate || undefined}
                onChange={(v) => setEndDate(v)}
                slotProps={{textField: {fullWidth: true}}}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Grund'
                fullWidth
                value={grund}
                onChange={(e) => setGrund(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Notiz'
                fullWidth
                multiline
                rows={3}
                value={notiz}
                onChange={(e) => setNotiz(e.target.value)}
              />
            </Grid>
            {workingDays !== null && (
              <Grid item xs={12}>
                <Card variant='outlined' sx={{backgroundColor: '#f5f9ff'}}>
                  <Box sx={{display: 'flex', alignItems: 'center', p: 2}}>
                    <Construction sx={{mr: 1, color: 'primary.main'}} />
                    <Typography variant='body1' fontWeight='medium'>
                      {workingDays} Arbeitstag{workingDays !== 1 ? 'e' : ''}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='inherit' disabled={loading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          color='primary'
          disabled={loading || invalidRange}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};
